// --- Build 1-ring adjacency once (from indexed geometry)
export function buildAdjacency(geom) {
    const idx = geom.index ? geom.index.array : null;
    const vcount = geom.attributes.position.count;
    const nbrs = Array(vcount).fill(null).map(() => new Set());
    if (!idx) {
      // generate triangles from sequential triplets if no index
      const n = Math.floor(vcount / 3) * 3;
      for (let i = 0; i < n; i += 3) {
        const a = i, b = i + 1, c = i + 2;
        nbrs[a].add(b).add(c); nbrs[b].add(a).add(c); nbrs[c].add(a).add(b);
      }
    } else {
      for (let i = 0; i < idx.length; i += 3) {
        const a = idx[i], b = idx[i + 1], c = idx[i + 2];
        nbrs[a].add(b).add(c); nbrs[b].add(a).add(c); nbrs[c].add(a).add(b);
      }
    }
    // pack sets to typed arrays for speed
    return nbrs.map(s => Uint32Array.from(s));
  }

  // --- Detect boundary vertices (vertices on edges that belong to only one triangle)
  export function detectBoundaryVertices(geom) {
    const idx = geom.index ? geom.index.array : null;
    const vcount = geom.attributes.position.count;
    const isBoundary = new Uint8Array(vcount); // 0 = interior, 1 = boundary

    // Map to count edge occurrences: "minVertex_maxVertex" -> count
    const edgeCount = new Map();

    const addEdge = (a, b) => {
      const min = Math.min(a, b);
      const max = Math.max(a, b);
      const key = `${min}_${max}`;
      edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
    };

    if (!idx) {
      // Non-indexed geometry
      const n = Math.floor(vcount / 3) * 3;
      for (let i = 0; i < n; i += 3) {
        const a = i, b = i + 1, c = i + 2;
        addEdge(a, b);
        addEdge(b, c);
        addEdge(c, a);
      }
    } else {
      // Indexed geometry
      for (let i = 0; i < idx.length; i += 3) {
        const a = idx[i], b = idx[i + 1], c = idx[i + 2];
        addEdge(a, b);
        addEdge(b, c);
        addEdge(c, a);
      }
    }

    // Mark vertices that have at least one edge with count = 1 (boundary edge)
    for (const [key, count] of edgeCount) {
      if (count === 1) {
        const [a, b] = key.split('_').map(Number);
        isBoundary[a] = 1;
        isBoundary[b] = 1;
      }
    }

    return isBoundary;
  }
  
  // --- Iteratively average normals with neighbors (few iters = smooth direction only)
  export function computeSmoothedVertexNormals(geom, neighbors, iters = 2) {
    const baseNor = geom.attributes.normal;
    if (!baseNor) geom.computeVertexNormals();
    const vcount = geom.attributes.position.count;
    const src = new Float32Array(vcount * 3);
    const dst = new Float32Array(vcount * 3);
    src.set(geom.attributes.normal.array);
  
    const normalize3 = (arr, i3) => {
      const x = arr[i3], y = arr[i3 + 1], z = arr[i3 + 2];
      const len = Math.hypot(x, y, z) || 1;
      arr[i3] = x / len; arr[i3 + 1] = y / len; arr[i3 + 2] = z / len;
    };
  
    for (let k = 0; k < iters; k++) {
      for (let i = 0; i < vcount; i++) {
        const i3 = i * 3;
        let ax = src[i3], ay = src[i3 + 1], az = src[i3 + 2];
        const nb = neighbors[i];
        for (let j = 0; j < nb.length; j++) {
          const j3 = nb[j] * 3;
          ax += src[j3]; ay += src[j3 + 1]; az += src[j3 + 2];
        }
        const inv = 1 / (nb.length + 1);
        dst[i3] = ax * inv; dst[i3 + 1] = ay * inv; dst[i3 + 2] = az * inv;
        normalize3(dst, i3);
      }
      // swap
      src.set(dst);
    }
    return src; // Float32Array length vcount*3
  }
  
  // --- Diffuse a scalar field (displacement) over the mesh graph with a soft mask
  export function smoothScalarField(field, neighbors, mask, iters = 4, alpha = 0.6) {
    // mask[i] in [0..1]; 1 = fully editable, 0 = fixed background.
    const vcount = field.length;
    const tmp = new Float32Array(vcount);
    for (let k = 0; k < iters; k++) {
      for (let i = 0; i < vcount; i++) {
        const m = mask[i];
        if (m <= 0.01) { tmp[i] = field[i]; continue; } // keep border locked
        const nb = neighbors[i];
        let sum = 0;
        for (let j = 0; j < nb.length; j++) sum += field[nb[j]];
        const avg = sum / Math.max(1, nb.length);
        // move partially toward neighborhood average
        tmp[i] = (1 - alpha) * field[i] + alpha * avg;
      }
      field.set(tmp);
    }
  }
  